///
/// EarthBrowser - Buoy Browser
///
/// Modern movable window for browsing ocean buoys using local OGC sync data
/// Created by Matt Giger on December 21, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { Switch } from '@/components/ui/switch'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { BuoyDataProvider } from '@/data-providers/BuoyDataProvider.tsx'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import type { DataRow } from '@/data-providers/types'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { BuoyObservationsTable } from '@/earthbrowser/dataset-distribution/buoys/BuoyObservationsTable'
import { BuoyStationsTable, type BuoyStationProperties } from '@/earthbrowser/dataset-distribution/buoys/BuoyStationsTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import { pushTargetToHistory } from '@/utils/navigationHistory'
import type { BuoyEntry } from '@shared-types/buoy'
import { AlertTriangle, Anchor } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface BuoyBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const BuoyBrowserRenderer = memo(function BuoyBrowserRenderer({
	windowId,
	windowState,
}: BuoyBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const buoysVisible = visibleDatasets.includes("buoy")

	// Get dataset layer manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state for query parameters and data
	const [buoyType, setBuoyType] = useState<string | undefined>(undefined)
	const [dartOnly, setDartOnly] = useState<boolean>(false)
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [buoys, setBuoys] = useState<BuoyStationProperties[]>([])
	const [types, setTypes] = useState<string[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [refreshTrigger, setRefreshTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Latest observation per station, joined into the rows so the Waves/Wind/
	// SST/Updated columns have live data. Kept in a ref (updated alongside
	// state) so the provider's queryFn always reads the current entries
	// instead of the stale closure it was created with.
	const entriesRef = useRef<BuoyEntry[]>([])

	// Load buoy types on mount
	useEffect(() => {
		const loadTypes = async () => {
			try {
				const loadedTypes = await BuoyStationsTable.getTypes()
				setTypes(loadedTypes)

				const count = await BuoyStationsTable.getCount()
				setTotalCount(count)
			} catch (error) {
				console.error('Failed to load buoy types:', error)
			}
		}

		void loadTypes()
	}, [])

	// Load stations plus the latest observation for each, joined into BuoyEntry
	// rows so the observation columns (Waves/Wind/SST/Updated) are populated
	const loadBuoys = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const [loadedBuoys, observations] = await Promise.all([
				dartOnly ? BuoyStationsTable.getDartBuoys()
					: buoyType ? BuoyStationsTable.getByType(buoyType)
					: BuoyStationsTable.getAll(),
				BuoyObservationsTable.getLatestByStation()
			])
			entriesRef.current = loadedBuoys.map(b => ({
				...b,
				latest_observation: observations.get(b.station_id) ?? null
			}) as BuoyEntry)
			setBuoys(loadedBuoys)
			setRefreshTrigger(prev => prev + 1)
		} catch (error) {
			console.error('Failed to load buoys:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [buoyType, dartOnly])

	// Load buoys when filters change (and on mount)
	useEffect(() => {
		void loadBuoys(true)
	}, [loadBuoys])

	// Reload silently whenever the buoys layer refreshes after a sync, so the
	// observation columns don't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('buoys', () => {
		void loadBuoys(false)
	})

	// Create a map of station ID to properties (for lookup)
	const buoyMap = useMemo(() => {
		const map = new Map<string, BuoyStationProperties>()
		for (const buoy of buoys) {
			map.set(buoy.station_id, buoy)
		}
		return map
	}, [buoys])

	// Initialize and update data provider
	useEffect(() => {
		// Don't do anything while loading
		if (isLoading) return

		// Only create provider if not initialized
		if (!isInitialized) {
			// Dispose old provider
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			const provider = new BuoyDataProvider({
				id: windowId,
				name: 'Buoys & Coastal Stations',
				// Read through the ref so refreshData() always sees current rows
				queryFn: async () => entriesRef.current
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			// Update existing provider with new data
			const provider = DataProviderRegistry.get(windowId) as BuoyDataProvider
			if (provider) {
				void provider.refreshData()
			}
		}
	}, [buoys, windowId, isLoading, isInitialized])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('buoys', buoysVisible)
		}
	}, [layerManager, buoysVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle buoy row click
	const handleRowClick = useCallback((row: DataRow) => {
		const buoy = row as unknown as BuoyEntry
		const stationId = String(buoy.station_id)

		// Look up the buoy to verify it exists
		const feature = buoyMap.get(stationId)
		if (!feature) {
			console.warn('Could not find buoy for:', stationId)
			return
		}

		// Ensure buoys are visible on the map
		if (!buoysVisible) {
			dispatch(sendDatasetAction({
				type: "buoy",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('buoys', true)
			}
		}

		// Focus on the buoy node
		if (layerManager) {
			layerManager.focusSceneObject('buoys', stationId)
		}

		// Deep link to buoy (use station_id without prefix for URL)
		pushTargetToHistory('buoy', stationId)

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, buoyMap, buoysVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadBuoys(true)
	}, [loadBuoys])

	// Header actions (filter selectors, DART toggle)
	const headerActions = useMemo(() => (
		<div className="flex items-center gap-2 p-1">
			{/* Type Filter */}
			{types.length > 0 && (
				<div className="flex items-center gap-1 bg-black/30 rounded-full p-1 pr-2 border border-white/10">
					<select
						value={buoyType || 'All'}
						onChange={(e) => setBuoyType(e.target.value === 'All' ? undefined : e.target.value)}
						className="px-3 py-1 text-xs rounded-full bg-transparent text-white/80 border-0 outline-none cursor-pointer hover:text-white"
					>
						<option value="All">All Types</option>
						{types.map((type) => (
							<option key={type} value={type}>{type}</option>
						))}
					</select>
				</div>
			)}

			{/* DART Only Toggle */}
			<div className="flex items-center gap-1.5 bg-black/30 rounded-full px-3 py-1.5 border border-white/10">
				<Switch
					checked={dartOnly}
					onCheckedChange={setDartOnly}
					className="data-[state=checked]:bg-orange-600 scale-75"
				/>
				<AlertTriangle className="w-3 h-3 text-orange-400" />
				<span className="text-xs text-white/80">DART</span>
			</div>
		</div>
	), [types, buoyType, dartOnly])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isInitialized && !isLoading) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`buoy-browser-${refreshTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search buoys by station ID, name, or location..."
				/>
			)
		}

		// Loading state
		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-cyan-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Buoys & Coastal Stations
					</p>
					<p className="text-sm text-white/60">
						{isLoading ? 'Fetching data...' : 'Initializing...'}
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, isLoading, handleRowClick, handleRefresh, totalCount, refreshTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Anchor className="w-5 h-5" />}
			iconColor="text-cyan-400"
			headerActions={headerActions}
			titlebarPrefix={<DatasetVisibilityToggle windowType="buoy" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})

